/**
 * ViewerView Component
 * Main viewer interface with gameplay area, webcam area, and chat
 * Subscribes to IVS Real-time stage to receive player streams
 */

import React, { useState, useEffect, useRef } from 'react';
import { StageConnectionState } from 'amazon-ivs-web-broadcast';
import { AppSyncChatClient } from '../utils/AppSyncChatClient';
import { IVSStageManager } from '../utils/IVSStageManager';
import { ChatComponent } from './ChatComponent';
import { BetOverlay } from './BetOverlay';
import { BalanceDisplay } from './BalanceDisplay';
import { CelebrationOverlay } from './CelebrationOverlay';
import { generateUsername } from '../utils/usernameGenerator';
import { APPSYNC_CONFIG } from '../utils/constants';
import { RemoteStageStream } from '../types/ivs.types';
import { useBettingState } from '../hooks/useBettingState';
import { useGameEvents } from '../hooks/useGameEvents';
import { ViewerBetEvent } from '../types/betting.types';

interface ViewerViewProps {
  user: any;
  signOut: any;
}

export const ViewerView: React.FC<ViewerViewProps> = ({ signOut }) => {
  // IVS Stage State
  const [gameplayStream, setGameplayStream] = useState<RemoteStageStream | null>(null);
  const [webcamStream, setWebcamStream] = useState<RemoteStageStream | null>(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isConnected, setIsConnected] = useState(false);

  // UI State
  const [username] = useState(generateUsername());
  const [errors, setErrors] = useState<string[]>([]);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    // Start with sidebar collapsed on mobile devices
    return window.innerWidth <= 768;
  });
  const [showPlayButton, setShowPlayButton] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Refs
  const gameplayVideoRef = useRef<HTMLVideoElement>(null);
  const webcamVideoRef = useRef<HTMLVideoElement>(null);
  const stageManagerRef = useRef<IVSStageManager>(new IVSStageManager());

  // Chat Client
  const [chatClient] = useState(() => new AppSyncChatClient(APPSYNC_CONFIG));

  // Betting State
  const {
    balance,
    currentBet,
    bettingEnabled,
    showBetOverlay,
    showCelebration,
    celebrationAmount,
    placeBet,
    calculatePayout,
    handleLoss,
    restoreBalance,
    setBettingEnabled,
    setShowBetOverlay,
    setShowCelebration,
  } = useBettingState();

  // Game Events
  const gameEvents = useGameEvents({ chatClient });

  // Initialize on mount
  useEffect(() => {
    // Copy refs to variables at effect creation time
    const stageManager = stageManagerRef.current;
    const client = chatClient;
    
    // Prevent body scroll
    document.body.style.overflow = 'hidden';
    
    // Don't auto-connect - wait for user to click play button

    // Cleanup on unmount
    return () => {
      if (stageManager.isActive()) {
        stageManager.leaveStage();
      }
      client.disconnect();
      // Restore body scroll
      document.body.style.overflow = '';
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Listen for fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  // Use refs to store latest values to avoid stale closures
  const currentBetRef = useRef(currentBet);
  const calculatePayoutRef = useRef(calculatePayout);
  const handleLossRef = useRef(handleLoss);
  const setBettingEnabledRef = useRef(setBettingEnabled);

  // Keep refs updated
  useEffect(() => {
    console.log('[ViewerView] Updating refs - currentBet:', currentBet, 'balance:', balance);
    currentBetRef.current = currentBet;
    calculatePayoutRef.current = calculatePayout;
    handleLossRef.current = handleLoss;
    setBettingEnabledRef.current = setBettingEnabled;
  }, [currentBet, calculatePayout, handleLoss, setBettingEnabled, balance]);

  // Register game event callbacks (only once)
  // Requirements: 3.1, 3.5
  useEffect(() => {
    // Handle GAME_SPIN_START: disable betting
    gameEvents.onGameSpinStart(() => {
      console.log('Game spin started - disabling betting');
      setBettingEnabledRef.current(false);
    });

    // Handle GAME_SPIN_END: enable betting
    gameEvents.onGameSpinEnd(() => {
      console.log('Game spin ended - enabling betting');
      setBettingEnabledRef.current(true);
    });

    // Handle GAME_WIN: calculate payout and trigger celebration if bet is active
    gameEvents.onGameWin((data) => {
      console.log('[ViewerView] GAME_WIN event received:', data);
      console.log('[ViewerView] Current bet from ref:', currentBetRef.current);
      
      // Only trigger celebration and payout if viewer has an active bet
      if (currentBetRef.current !== null) {
        console.log('[ViewerView] Active bet found, calculating payout with multiplier:', data.payMultiplier);
        console.log('[ViewerView] Calling calculatePayout...');
        calculatePayoutRef.current(data.payMultiplier);
        console.log('[ViewerView] calculatePayout called');
      } else {
        console.log('[ViewerView] No active bet, skipping payout');
      }
    });

    // Handle GAME_LOSE: clear bet without celebration
    gameEvents.onGameLose(() => {
      console.log('[ViewerView] GAME_LOSE event received');
      console.log('[ViewerView] Current bet from ref:', currentBetRef.current);
      
      // Clear the current bet (balance was already deducted)
      if (currentBetRef.current !== null) {
        console.log('[ViewerView] Clearing active bet after loss');
        handleLossRef.current();
      }
    });
  }, [gameEvents]);

  /**
   * Handle subscribe button click
   */
  const handleSubscribe = async () => {
    try {
      const subscribeEvent = {
        action: 'STREAM_SUBSCRIBE',
        message: '',
        user: username,
        timestamp: Date.now()
      };

      // Publish subscribe event using publishRaw
      await (chatClient as any).publishRaw(subscribeEvent);
      
      console.log('Sent subscribe event');
      
      // Update button state
      setIsSubscribed(true);
    } catch (error) {
      console.error('Failed to send subscribe event:', error);
    }
  };

  /**
   * Handle bet placement
   * Requirements: 1.4, 6.1, 6.2, 6.3, 6.4, 6.5
   */
  const handlePlaceBet = async (amount: number) => {
    // Deduct balance immediately
    placeBet(amount);

    try {
      // Create VIEWER_BET event with correct structure
      const viewerBetEvent: ViewerBetEvent = {
        action: 'VIEWER_BET',
        message: {
          username: username,
          totalBet: amount,
        },
        timestamp: Date.now(),
      };

      // Publish event using AppSync client's publishRaw method
      await (chatClient as any).publishRaw(viewerBetEvent);
      
      console.log('Bet placed successfully:', amount);
      
      // Handle publish success: close overlay
      setShowBetOverlay(false);
    } catch (error) {
      console.error('Failed to place bet:', error);
      
      // Handle publish failure: restore balance and show error
      restoreBalance(amount);
      setErrors(prev => [...prev, 'Failed to place bet. Please try again.']);
    }
  };

  /**
   * Handle play button click - start connection
   */
  const handlePlayClick = () => {
    setShowPlayButton(false);
    connectToStage();
  };

  /**
   * Toggle fullscreen mode
   */
  const toggleFullScreen = () => {
    const containerElement = document.getElementById('ViewerGameplayContainer');
    
    if (!document.fullscreenElement) {
      // Enter fullscreen
      if (containerElement) {
        containerElement.requestFullscreen().catch((error) => {
          console.warn('Fullscreen request failed:', error);
        });
        
        // @ts-ignore
        if (navigator.keyboard) {
          // @ts-ignore
          const keyboard = navigator.keyboard;
          keyboard.lock(['Escape']).catch(() => {});
        }
      }
    } else {
      // Exit fullscreen
      document.exitFullscreen();
    }
  };

  /**
   * Connect to IVS stage as a subscriber
   * Implements subtask 7.2: Implement IVS stage subscription
   */
  const connectToStage = async () => {
    if (isConnecting || isConnected) {
      return;
    }

    setIsConnecting(true);
    setErrors([]);

    try {
      // Fetch participant token for viewer with SUBSCRIBE capability
      const participantToken = await stageManagerRef.current.fetchParticipantToken(
        username,
        ['SUBSCRIBE']
      );

      // Create and configure the stage
      const newStage = await stageManagerRef.current.createStage({
        participantToken,
        streams: [], // Viewers don't publish streams
        onConnectionStateChange: (state) => {
          console.log('Viewer stage connection state:', state);
          if (state === StageConnectionState.CONNECTED) {
            console.log('Successfully connected to IVS stage as viewer');
            setIsConnected(true);
            setIsConnecting(false);
          } else if (state === StageConnectionState.DISCONNECTED) {
            setIsConnected(false);
            setGameplayStream(null);
            setWebcamStream(null);
          }
        },
        onStreamsAdded: (streams) => {
          console.log('Streams added:', streams);
          handleStreamsAdded(streams);
        },
        onError: (error) => {
          console.error('Viewer stage error:', error);
          setErrors(prev => [...prev, `Stage error: ${error.message}`]);
          setIsConnecting(false);
        }
      });

      // Join the stage
      await stageManagerRef.current.joinStage();

      console.log('Successfully joined IVS stage as viewer', newStage);
    } catch (error) {
      console.error('Failed to connect to IVS stage:', error);
      setErrors(prev => [...prev, `Failed to connect: ${error instanceof Error ? error.message : 'Unknown error'}`]);
      setIsConnecting(false);
    }
  };

  /**
   * Handle streams being added
   * Implements subtask 7.3: Implement stream identification and rendering
   * Identifies streams by stream_source attribute and assigns to appropriate video elements
   * Properly handles multiple tracks (audio/video) for the same source
   */
  const handleStreamsAdded = (streams: RemoteStageStream[]) => {
    streams.forEach((stream: any) => {
      // Get participant info attached by IVSStageManager
      const participantInfo = stream._participantInfo;
      const streamSource = participantInfo?.attributes?.stream_source;
      const mediaStreamTrack = stream.mediaStreamTrack;

      if (!mediaStreamTrack) {
        console.warn('Stream has no mediaStreamTrack');
        return;
      }

      const trackKind = mediaStreamTrack.kind; // 'audio' or 'video'
      console.log(`Processing ${trackKind} track with source:`, streamSource, 'from participant:', participantInfo?.userId);

      // Assign track based on stream_source attribute
      if (streamSource === 'gameplay') {
        setGameplayStream(stream);
        
        // Get or create MediaStream for gameplay video element
        if (gameplayVideoRef.current) {
          let existingMediaStream = gameplayVideoRef.current.srcObject as MediaStream;
          
          if (!existingMediaStream) {
            // Create new MediaStream if none exists
            existingMediaStream = new MediaStream();
            gameplayVideoRef.current.srcObject = existingMediaStream;
          }
          
          // Add the track if it's not already present
          const existingTracks = existingMediaStream.getTracks();
          const trackExists = existingTracks.some(t => t.id === mediaStreamTrack.id);
          
          if (!trackExists) {
            existingMediaStream.addTrack(mediaStreamTrack);
            console.log(`✓ Added ${trackKind} track to gameplay stream`);
          }
        }
      } else if (streamSource === 'player_webcam') {
        setWebcamStream(stream);
        
        // Get or create MediaStream for webcam video element
        if (webcamVideoRef.current) {
          let existingMediaStream = webcamVideoRef.current.srcObject as MediaStream;
          
          if (!existingMediaStream) {
            // Create new MediaStream if none exists
            existingMediaStream = new MediaStream();
            webcamVideoRef.current.srcObject = existingMediaStream;
          }
          
          // Add the track if it's not already present
          const existingTracks = existingMediaStream.getTracks();
          const trackExists = existingTracks.some(t => t.id === mediaStreamTrack.id);
          
          if (!trackExists) {
            existingMediaStream.addTrack(mediaStreamTrack);
            console.log(`✓ Added ${trackKind} track to webcam stream`);
          }
        }
      } else {
        // Fallback: If no stream_source attribute, assign by order
        console.warn('Stream has no stream_source attribute, using fallback assignment');
        
        if (!gameplayStream && gameplayVideoRef.current) {
          setGameplayStream(stream);
          let existingMediaStream = gameplayVideoRef.current.srcObject as MediaStream;
          
          if (!existingMediaStream) {
            existingMediaStream = new MediaStream();
            gameplayVideoRef.current.srcObject = existingMediaStream;
          }
          
          existingMediaStream.addTrack(mediaStreamTrack);
          console.log(`Assigned ${trackKind} track to gameplay (fallback)`);
        } else if (!webcamStream && webcamVideoRef.current) {
          setWebcamStream(stream);
          let existingMediaStream = webcamVideoRef.current.srcObject as MediaStream;
          
          if (!existingMediaStream) {
            existingMediaStream = new MediaStream();
            webcamVideoRef.current.srcObject = existingMediaStream;
          }
          
          existingMediaStream.addTrack(mediaStreamTrack);
          console.log(`Assigned ${trackKind} track to webcam (fallback)`);
        }
      }
    });
  };

  // Note: We no longer need useEffect hooks to update video elements
  // because handleStreamsAdded now properly manages MediaStream tracks

  return (
    <>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          @keyframes pulse {
            0% { opacity: 1; }
            50% { opacity: 0.5; }
            100% { opacity: 1; }
          }
          .viewer-view-container {
            display: flex;
            flex-direction: column;
            height: 100vh;
            background: linear-gradient(135deg, #0a0a0a 0%, #1a0a0a 50%, #0a0a0a 100%);
            overflow: hidden;
          }
          .viewer-view-header {
            background: linear-gradient(90deg, #1a0000 0%, #2d0a0a 50%, #1a0000 100%);
            border-bottom: 2px solid #d4af37;
            padding: 1rem;
            display: flex;
            justify-content: space-between;
            align-items: center;
            box-shadow: 0 4px 12px rgba(212, 175, 55, 0.3);
          }
          .viewer-view-header h2 {
            color: #d4af37;
            margin: 0;
            font-size: 1.5rem;
            text-shadow: 0 0 10px rgba(212, 175, 55, 0.5);
          }
          .viewer-view-header .username {
            color: #ff4444;
            font-size: 1rem;
            text-shadow: 0 0 8px rgba(255, 68, 68, 0.5);
          }
          .viewer-view-content {
            display: flex;
            flex: 1;
            overflow: hidden;
          }
          .gameplay-area {
            flex: 1;
            display: flex;
            flex-direction: column;
            padding: 1rem;
            background: linear-gradient(135deg, #0a0a0a 0%, #1a0a0a 100%);
          }
          .sidebar {
            width: 425px;
            display: flex;
            flex-direction: column;
            background: linear-gradient(180deg, #1a0000 0%, #0d0000 100%);
            border-left: 2px solid #d4af37;
            transition: transform 0.3s ease;
          }
          .sidebar.collapsed {
            position: fixed;
            right: 0;
            top: 0;
            bottom: 0;
            transform: translateX(100%);
            z-index: 20;
          }
          .sidebar-header {
            background-color: rgba(26, 0, 0, 0.8);
            padding: 0.75rem 1rem;
            display: flex;
            justify-content: space-between;
            align-items: center;
            border-bottom: 2px solid #d4af37;
          }
          .sidebar-header h3 {
            color: #d4af37;
            margin: 0;
            font-size: 1rem;
            font-weight: 600;
            text-shadow: 0 0 8px rgba(212, 175, 55, 0.5);
          }
          .collapse-btn {
            background: rgba(255, 255, 255, 0.1);
            backdrop-filter: blur(5px);
            -webkit-backdrop-filter: blur(5px);
            border: 1px solid rgba(255, 255, 255, 0.2);
            color: white;
            width: 32px;
            height: 32px;
            border-radius: 6px;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            transition: all 0.2s ease;
            opacity: 0.7;
          }
          .collapse-btn:hover {
            opacity: 1;
            background: rgba(255, 255, 255, 0.2);
            transform: scale(1.05);
          }
          .collapse-btn i {
            font-size: 14px;
          }
          .expand-sidebar-btn {
            background: rgba(0, 0, 0, 0.3);
            backdrop-filter: blur(5px);
            -webkit-backdrop-filter: blur(5px);
            border: 1px solid rgba(255, 255, 255, 0.2);
            color: white;
            width: 36px;
            height: 36px;
            border-radius: 6px;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            transition: all 0.2s ease;
            opacity: 0.6;
            flex-shrink: 0;
          }
          .expand-sidebar-btn:hover {
            opacity: 1;
            background: rgba(0, 0, 0, 0.5);
            transform: scale(1.05);
          }
          .expand-sidebar-btn i {
            font-size: 16px;
          }
          .video-container {
            position: relative;
            background-color: #000;
            border-radius: 8px;
            overflow: hidden;
            border: 2px solid #d4af37;
            box-shadow: 0 0 20px rgba(212, 175, 55, 0.3);
          }
          .gameplay-video-container {
            flex: 1;
            position: relative;
          }
          .webcam-video-container {
            height: 200px;
            margin: 1rem 1rem 0 1rem;
            border: 2px solid #d4af37;
            box-shadow: 0 0 15px rgba(212, 175, 55, 0.4);
            max-width: 100%;
            position: relative;
          }
          .webcam-video-container .stream-status {
            position: absolute;
            top: 12px;
            right: 12px;
          }
          .video-container video {
            width: 100%;
            height: 100%;
            object-fit: contain;
          }
          .webcam-video-container video {
            object-fit: cover;
          }
          .top-right-controls {
            position: absolute;
            top: 12px;
            right: 12px;
            display: flex;
            align-items: center;
            gap: 0.5rem;
            z-index: 15;
          }
          .stream-status {
            background: rgba(26, 0, 0, 0.9);
            backdrop-filter: blur(10px);
            -webkit-backdrop-filter: blur(10px);
            border: 2px solid #d4af37;
            padding: 0.4rem 0.875rem;
            border-radius: 20px;
            display: inline-flex;
            align-items: center;
            gap: 0.5rem;
            color: #d4af37;
            font-size: 0.875rem;
            font-weight: 500;
            box-shadow: 0 0 15px rgba(212, 175, 55, 0.5);
            white-space: nowrap;
          }
          .status-dot {
            width: 8px;
            height: 8px;
            border-radius: 50%;
            background-color: #666;
            flex-shrink: 0;
          }
          .status-dot.live {
            background-color: #32cd32;
            box-shadow: 0 0 8px rgba(50, 205, 50, 0.8);
          }
          .status-dot.inactive {
            background-color: #666;
          }
          .loading-overlay {
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(26, 26, 46, 0.85);
            backdrop-filter: blur(8px);
            -webkit-backdrop-filter: blur(8px);
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            color: white;
            z-index: 10;
          }
          .spinner {
            width: 40px;
            height: 40px;
            border: 4px solid rgba(255, 255, 255, 0.3);
            border-top: 4px solid white;
            border-radius: 50%;
            animation: spin 1s linear infinite;
            margin-bottom: 1rem;
          }
          .offline-message {
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            text-align: center;
            color: #d4af37;
            z-index: 5;
          }
          .offline-message h3 {
            color: #ff4444;
            margin-bottom: 0.5rem;
            text-shadow: 0 0 10px rgba(255, 68, 68, 0.5);
          }
          .offline-message p {
            color: #d4af37;
            font-size: 0.9rem;
          }
          .chat-container {
            flex: 1;
            display: flex;
            flex-direction: column;
            overflow: hidden;
            padding: 1rem;
          }
          .error-banner {
            background: rgba(139, 0, 0, 0.9);
            backdrop-filter: blur(10px);
            -webkit-backdrop-filter: blur(10px);
            border: 2px solid #ff4444;
            color: #fff;
            padding: 0.5rem 1rem;
            margin: 0.5rem 1rem;
            border-radius: 8px;
            font-size: 0.9rem;
            box-shadow: 0 0 15px rgba(255, 68, 68, 0.4);
          }
          .sign-out-button {
            background: linear-gradient(135deg, #1a0000 0%, #2d0a0a 100%);
            backdrop-filter: blur(10px);
            -webkit-backdrop-filter: blur(10px);
            border: 2px solid #d4af37;
            color: #d4af37;
            padding: 0.5rem 1rem;
            border-radius: 8px;
            cursor: pointer;
            transition: all 0.2s;
            box-shadow: 0 0 10px rgba(212, 175, 55, 0.3);
          }
          .sign-out-button:hover {
            background: linear-gradient(135deg, #2d0a0a 0%, #3d1a1a 100%);
            border-color: #ffd700;
            transform: translateY(-1px);
            box-shadow: 0 0 20px rgba(255, 215, 0, 0.5);
            color: #ffd700;
          }
          
          /* Subscribe Button */
          .subscribe-button {
            position: absolute;
            bottom: 20px;
            right: 20px;
            background: linear-gradient(135deg, #8b0000 0%, #dc143c 100%);
            backdrop-filter: blur(10px);
            -webkit-backdrop-filter: blur(10px);
            border: 2px solid #ff4444;
            color: white;
            padding: 12px 24px;
            border-radius: 24px;
            font-size: 16px;
            font-weight: 600;
            cursor: pointer;
            display: flex;
            align-items: center;
            gap: 8px;
            z-index: 10;
            transition: all 0.3s ease;
            box-shadow: 0 0 20px rgba(255, 68, 68, 0.5);
          }
          .subscribe-button:hover:not(:disabled) {
            background: linear-gradient(135deg, #dc143c 0%, #ff4444 100%);
            transform: translateY(-2px);
            box-shadow: 0 0 30px rgba(255, 68, 68, 0.7);
          }
          .subscribe-button:active:not(:disabled) {
            transform: translateY(0);
          }
          .subscribe-button:disabled {
            background: linear-gradient(135deg, #006400 0%, #228b22 100%);
            border-color: #32cd32;
            cursor: default;
            box-shadow: 0 0 20px rgba(50, 205, 50, 0.5);
          }
          .subscribe-button i {
            font-size: 18px;
          }
          
          /* Floating Bet Button (when sidebar collapsed) */
          .floating-bet-btn {
            position: absolute;
            bottom: 20px;
            left: 50%;
            transform: translateX(-50%);
            background: linear-gradient(135deg, #8b0000 0%, #dc143c 100%);
            backdrop-filter: blur(10px);
            -webkit-backdrop-filter: blur(10px);
            border: 2px solid #ff4444;
            color: white;
            padding: 12px 24px;
            border-radius: 24px;
            font-size: 16px;
            font-weight: 600;
            cursor: pointer;
            display: flex;
            align-items: center;
            gap: 8px;
            z-index: 15;
            transition: all 0.3s ease;
            box-shadow: 0 0 20px rgba(255, 68, 68, 0.5);
          }
          .floating-bet-btn:hover:not(:disabled) {
            background: linear-gradient(135deg, #dc143c 0%, #ff4444 100%);
            transform: translateX(-50%) translateY(-2px);
            box-shadow: 0 0 30px rgba(255, 68, 68, 0.7);
          }
          .floating-bet-btn:active:not(:disabled) {
            transform: translateX(-50%) translateY(0);
          }
          .floating-bet-btn:disabled {
            background: linear-gradient(135deg, #2f2f2f 0%, #4a4a4a 100%);
            border-color: #666;
            cursor: not-allowed;
            opacity: 0.6;
          }
          .floating-bet-btn i {
            font-size: 18px;
          }
          
          /* Fullscreen Toggle Button */
          .fullscreen-toggle-btn {
            position: absolute;
            bottom: 20px;
            right: 20px;
            background: rgba(0, 0, 0, 0.3);
            backdrop-filter: blur(5px);
            -webkit-backdrop-filter: blur(5px);
            border: 1px solid rgba(255, 255, 255, 0.2);
            color: white;
            width: 48px;
            height: 48px;
            border-radius: 24px;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 15;
            transition: all 0.2s ease;
            opacity: 0.6;
          }
          .fullscreen-toggle-btn:hover {
            opacity: 1;
            background: rgba(0, 0, 0, 0.5);
            transform: scale(1.05);
          }
          .fullscreen-toggle-btn i {
            font-size: 18px;
          }
          
          /* Bet Modal Dialog */
          .bet-modal-backdrop {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0, 0, 0, 0.7);
            backdrop-filter: blur(5px);
            -webkit-backdrop-filter: blur(5px);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 9999;
            animation: fadeIn 0.2s ease-out;
            padding: 1rem;
          }
          @keyframes fadeIn {
            from {
              opacity: 0;
            }
            to {
              opacity: 1;
            }
          }
          .bet-modal-dialog {
            width: 90%;
            max-width: 450px;
            animation: slideIn 0.3s ease-out;
          }
          @keyframes slideIn {
            from {
              opacity: 0;
              transform: scale(0.9) translateY(-20px);
            }
            to {
              opacity: 1;
              transform: scale(1) translateY(0);
            }
          }
          .bet-modal-dialog .bet-overlay {
            max-height: none;
            border: 2px solid #d4af37;
            border-radius: 12px;
            margin-bottom: 0;
          }
          
          /* Mobile Responsive Styles */
          @media (max-width: 768px) {
            .bet-modal-dialog {
              width: 92%;
              max-width: 360px;
            }
            
            .fullscreen-toggle-btn {
              width: 44px;
              height: 44px;
            }
            
            .floating-bet-btn {
              padding: 10px 20px;
              font-size: 14px;
            }
          }
          
          @media (max-width: 480px) {
            .bet-modal-dialog {
              width: 90%;
              max-width: 320px;
            }
            
            .fullscreen-toggle-btn {
              width: 40px;
              height: 40px;
            }
            
            .fullscreen-toggle-btn i {
              font-size: 16px;
            }
            
            .floating-bet-btn {
              padding: 8px 16px;
              font-size: 13px;
            }
          }
          
          @media (max-width: 380px) {
            .bet-modal-dialog {
              width: 88%;
              max-width: 300px;
            }
            
            .fullscreen-toggle-btn {
              width: 38px;
              height: 38px;
            }
            
            .floating-bet-btn {
              padding: 8px 14px;
              font-size: 12px;
            }
          }
          
          /* Play Button Overlay */
          .play-button-overlay {
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0, 0, 0, 0.8);
            backdrop-filter: blur(10px);
            -webkit-backdrop-filter: blur(10px);
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            z-index: 20;
            cursor: pointer;
            transition: background 0.3s ease;
          }
          .play-button-overlay:hover {
            background: rgba(0, 0, 0, 0.9);
          }
          .play-button {
            width: 120px;
            height: 120px;
            border-radius: 50%;
            background: linear-gradient(135deg, #8b0000 0%, #dc143c 100%);
            border: 4px solid #d4af37;
            display: flex;
            align-items: center;
            justify-content: center;
            transition: all 0.3s ease;
            box-shadow: 0 0 40px rgba(212, 175, 55, 0.6);
            margin-bottom: 1.5rem;
          }
          .play-button-overlay:hover .play-button {
            transform: scale(1.1);
            box-shadow: 0 0 60px rgba(212, 175, 55, 0.8);
            background: linear-gradient(135deg, #dc143c 0%, #ff4444 100%);
          }
          .play-button i {
            font-size: 48px;
            color: #d4af37;
            margin-left: 8px;
          }
          .play-button-text {
            color: #d4af37;
            font-size: 1.5rem;
            font-weight: 600;
            text-shadow: 0 0 10px rgba(212, 175, 55, 0.5);
          }
          .play-button-subtext {
            color: #999;
            font-size: 0.9rem;
            margin-top: 0.5rem;
          }
        `}
      </style>

      <div className="viewer-view-container">
        {/* Header */}
        <div className="viewer-view-header">
          <div>
            <h2>Casino Gaming Streams (Viewer)</h2>
            <span className="username">@{username}</span>
          </div>
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
            <BalanceDisplay balance={balance} />
            <button className="sign-out-button" onClick={signOut}>
              Sign Out
            </button>
          </div>
        </div>

        {/* Error Messages */}
        {errors.length > 0 && (
          <div>
            {errors.slice(-3).map((error, index) => (
              <div key={index} className="error-banner" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>{error}</span>
                <button
                  onClick={() => setErrors(prev => prev.filter((_, i) => i !== prev.length - 3 + index))}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'white',
                    cursor: 'pointer',
                    fontSize: '1.2rem',
                    padding: '0 0.5rem',
                    marginLeft: '1rem'
                  }}
                  aria-label="Dismiss error"
                >
                  ×
                </button>
              </div>
            ))}
            {errors.length > 3 && (
              <div className="error-banner" style={{ fontSize: '0.8rem', opacity: 0.8 }}>
                + {errors.length - 3} more error{errors.length - 3 !== 1 ? 's' : ''}
                <button
                  onClick={() => setErrors([])}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'white',
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    marginLeft: '0.5rem'
                  }}
                >
                  Clear all
                </button>
              </div>
            )}
          </div>
        )}

        {/* Main Content */}
        <div className="viewer-view-content">
          {/* Gameplay Area (Area 1) */}
          <div className="gameplay-area">
            <div className="gameplay-video-container video-container" id="ViewerGameplayContainer">
              {/* Celebration Overlay - Inside fullscreen container */}
              <CelebrationOverlay
                show={showCelebration}
                winAmount={celebrationAmount}
                onComplete={() => setShowCelebration(false)}
              />

              {/* Top Right Controls */}
              <div className="top-right-controls">
                {/* Stream Status Indicator */}
                {isConnected && (
                  <div className="stream-status">
                    <div className={`status-dot ${gameplayStream ? 'live' : 'inactive'}`}></div>
                    <span>{gameplayStream ? 'Connected' : 'Connecting...'}</span>
                  </div>
                )}

                {/* Expand Sidebar Button */}
                {isSidebarCollapsed && (
                  <button
                    className="expand-sidebar-btn"
                    onClick={() => setIsSidebarCollapsed(false)}
                    title="Show chat"
                  >
                    <i className="bi bi-chat-left-text" style={{color: '#d4af37'}}></i>
                  </button>
                )}
              </div>

              {/* Subscribe Button */}
              {isConnected && gameplayStream && !isSidebarCollapsed && (
                <button
                  className="subscribe-button"
                  onClick={handleSubscribe}
                  disabled={isSubscribed}
                >
                  {isSubscribed ? (
                    <>
                      <i className="bi bi-check-circle-fill"></i>
                      <span>Subscribed</span>
                    </>
                  ) : (
                    <>
                      <i className="bi bi-bell-fill"></i>
                      <span>Subscribe</span>
                    </>
                  )}
                </button>
              )}

              {/* Fullscreen Toggle Button (when sidebar collapsed) */}
              {isSidebarCollapsed && gameplayStream && (
                <button
                  className="fullscreen-toggle-btn"
                  onClick={toggleFullScreen}
                  title={isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'}
                >
                  <i className={`bi ${isFullscreen ? 'bi-fullscreen-exit' : 'bi-arrows-fullscreen'}`}></i>
                </button>
              )}

              {/* Floating Bet Button (when sidebar collapsed) */}
              {isSidebarCollapsed && gameplayStream && (
                <button
                  className="floating-bet-btn"
                  onClick={() => setShowBetOverlay(true)}
                  disabled={!bettingEnabled}
                  title="Place a bet"
                >
                  <i className="bi bi-coin"></i>
                  <span>Place Bet</span>
                </button>
              )}

              {/* Gameplay Video */}
              <video
                ref={gameplayVideoRef}
                autoPlay
                playsInline
                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
              />

              {/* Play Button Overlay */}
              {showPlayButton && (
                <div className="play-button-overlay" onClick={handlePlayClick}>
                  <div className="play-button">
                    <i className="bi bi-play-fill"></i>
                  </div>
                  <div className="play-button-text">Click to Watch Stream</div>
                  <div className="play-button-subtext">Interaction required to play video</div>
                </div>
              )}

              {/* Loading Overlay */}
              {isConnecting && (
                <div className="loading-overlay">
                  <div className="spinner"></div>
                  <div>Connecting to stream...</div>
                </div>
              )}

              {/* Offline Message */}
              {!isConnecting && !gameplayStream && isConnected && (
                <div className="offline-message">
                  <h3>No Stream Available</h3>
                  <p>The player is currently offline or not broadcasting.</p>
                  <p style={{ fontSize: '0.8rem', marginTop: '1rem' }}>
                    Waiting for player to start streaming...
                  </p>
                </div>
              )}

              {/* Not Connected Message */}
              {!isConnecting && !isConnected && (
                <div className="offline-message">
                  <h3>Connection Failed</h3>
                  <p>Unable to connect to the streaming stage.</p>
                  <p style={{ fontSize: '0.8rem', marginTop: '1rem' }}>
                    Please refresh the page to try again.
                  </p>
                </div>
              )}

              {/* Bet Modal Dialog (when sidebar collapsed) - Inside fullscreen container */}
              {showBetOverlay && isSidebarCollapsed && (
                <div 
                  className="bet-modal-backdrop"
                  onClick={() => setShowBetOverlay(false)}
                >
                  <div 
                    className="bet-modal-dialog"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <BetOverlay
                      balance={balance}
                      onPlaceBet={handlePlaceBet}
                      onClose={() => setShowBetOverlay(false)}
                      bettingEnabled={bettingEnabled}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className={`sidebar ${isSidebarCollapsed ? 'collapsed' : ''}`}>
            {/* Sidebar Header */}
            <div className="sidebar-header p-0">
              <button
                className="collapse-btn border-0 bg-transparent"
                onClick={() => setIsSidebarCollapsed(true)}
                title="Hide chat"
              >
                <i className="bi bi-chevron-right" style={{color: '#d4af37'}}></i>
              </button>
            </div>

            {/* Webcam Area (Area 2) */}
            <div className="webcam-video-container video-container">
              {/* Stream Status Indicator */}
              {isConnected && (
                <div className="stream-status">
                  <div className={`status-dot ${webcamStream ? 'live' : 'inactive'}`}></div>
                  <span>{webcamStream ? 'Webcam On' : 'Webcam Off'}</span>
                </div>
              )}

              {/* Webcam Video */}
              <video
                ref={webcamVideoRef}
                autoPlay
                playsInline
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />

              {/* Offline Message */}
              {!webcamStream && isConnected && (
                <div className="offline-message">
                  <p style={{ fontSize: '0.9rem' }}>Player webcam not active</p>
                </div>
              )}
            </div>

            {/* Chat Area (Area 3) */}
            <div className="chat-container" style={{ position: 'relative' }}>
              {/* Bet Overlay - only show in chat when sidebar is visible */}
              {showBetOverlay && !isSidebarCollapsed && (
                <div
                  style={{
                    position: 'absolute',
                    bottom: '60px',
                    left: '1rem',
                    right: '1rem',
                    maxHeight: '400px',
                    zIndex: 100,
                  }}
                >
                  <BetOverlay
                    balance={balance}
                    onPlaceBet={handlePlaceBet}
                    onClose={() => setShowBetOverlay(false)}
                    bettingEnabled={bettingEnabled}
                  />
                </div>
              )}
              
              <ChatComponent
                username={username}
                chatClient={chatClient}
                showBetButton={true}
                onBetButtonClick={() => setShowBetOverlay(!showBetOverlay)}
                bettingEnabled={true}// temp commented out {bettingEnabled && gameplayStream !== null}
                emotesEnabled={true}// temp commented out {gameplayStream !== null}
                chatEnabled={true}// temp commented out {gameplayStream !== null}
                enableAutoScroll={!isSidebarCollapsed}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ViewerView;
